export const frontmatter = {
  title: "On Mark's llama-verse",
  description: "Zuckerberg is all-in on LLaMA. What happened to the metaverse, and why is open-source AI suddenly Meta's big bet?",
  date: "2024-01-07",
  tags: ["ai", "opinions", "industry"],
  readingTime: 5,
};

LLaMA has quietly become a favorite among devs. Maybe not *the* favorite, but definitely a top pick. Why? It's open-source, which makes it a playground — you can experiment, customize, fine-tune, run it on your own hardware. Most of the well-known models (Claude, GPT) sit behind paywalls and APIs. LLaMA you can just pick up.

There's a small catch: if you build a business on top of it that crosses **$700M in revenue**, Meta wants you to get a commercial license. Honestly — at $700M, that's not the worst problem to have.

> "Open-source AI is the future. By making LLaMA accessible, Meta isn't just shipping tech, it's growing a community."  
> — every tech analyst, basically

## Open-source actually changes the game

I keep calling open-source a game-changer because it lets people build things the original team would never have thought of. Devs can tweak LLaMA in directions Meta doesn't have to fund or approve. That's where most of the interesting GenAI work is happening right now — independent builders with no permission required.

There's a real cost trade-off though. Running LLaMA on an A100 GPU works out to roughly $4.20 per million tokens. OpenAI's GPT-3.5 Turbo is about $2 per million. Cheaper, on paper.

- **Hardware adds up.** Good GPUs aren't cheap to rent or own.
- **Customization isn't free.** The flexibility you love comes with engineering hours nobody's paying you for.

<Callout>
  GPT-4 is around $60 per million tokens, so for many workloads LLaMA still wins. But "self-hosted = always cheaper" isn't true. Run the math for your actual usage.
</Callout>

## Where did the metaverse go?

Quick reminder: Facebook renamed itself **Meta** because Zuck thought the metaverse was the next big thing. A virtual world where we'd live, work, play, hang out as avatars.

That hasn't really happened. Despite billions in spend, the metaverse is still niche. The stock took a beating for it.

> "The metaverse is still a niche market. Until VR is as common as smartphones, it might stay a lofty dream."  
> — paraphrasing about a hundred analysts

A few reasons it's stalled:

- **Headsets are expensive.** A serious VR setup is still around $1,000.
- **The tech isn't quite there.** Building a smooth, immersive virtual world is genuinely hard.
- **Adoption is low.** Roughly 10% of US adults own a VR headset, per Pew.

## Where AI fits in

Mark hasn't given up on the metaverse — he's pairing it with LLaMA. The pitch is roughly:

- **Better worlds.** AI-generated environments, smarter NPCs, real-feeling interactions.
- **Easier to build.** Tools that let creators make worlds without an army of artists.

If both bets pay off, the metaverse stops being a barren tech demo and starts being something people actually want to spend time in.

## Bold move or rescue mission?

Hard to tell. Meta is using AI to patch the metaverse's biggest problem (it's empty and boring). If that works, it's brilliant. If it doesn't, they've spent a decade and a fortune on something that didn't land.

The risks are real:

- **Misuse.** Open-source AI can be weaponized — deepfakes, social engineering at scale.
- **Resource split.** Funding two huge bets at once is hard, even for Meta.

The metaverse still feels early. Without a real consumer breakthrough — probably cheaper, lighter hardware — it might stay a niche.

So Mark's running a high-stakes parlay: bet on LLaMA, bet on the metaverse, hope at least one wins big enough to carry the other. Personally, LLaMA looks like the easier sell. The metaverse needs a hardware revolution before it has a software one.

But Meta's "obviously crazy" bets have a habit of looking obvious in hindsight. Acquiring Instagram looked dumb at $1B. WhatsApp at $19B looked dumber. Both turned out to be steals. So I wouldn't write off either side of this one yet.
